import { useState } from 'react';
import { Box, Typography, TextField, Paper, Container, Avatar, InputAdornment, Chip, Card, CardContent, IconButton } from '@mui/material';
import { Person, Phone, VpnKey, Send, CheckCircle, ArrowBack } from '@mui/icons-material';
import { Error as ErrorIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './AddNewEntry.module.css';
import { useLocation, useNavigate } from 'react-router';
import { marketPlaceSendOtpApi, marketPlaceVerifyOtpApi, storeMarketCustomerApi } from 'utils/API/Merchant APIs/MerchantApiCalls';
import { LoadingButton } from '@mui/lab';
import { globalNotifySuccess } from 'views/utilities/AlertToast';

const AddNewCustomerEntry = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [step, setStep] = useState('input');
    const [otp, setOtp] = useState({ value: '', loading: false });
    const [otpError, setOtpError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');

    const displayData = location?.state?.service;

    // Validation schema
    const validationSchema = Yup.object({
        customerName: Yup.string()
            .min(2, 'Name must be at least 2 characters')
            .max(50, 'Name must be less than 50 characters')
            .matches(/^[a-zA-Z\s]+$/, 'Name should only contain letters and spaces')
            .required('Customer name is required'),
        phoneNumber: Yup.string()
            .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits')
            .required('Phone number is required')
    });

    // Formik setup
    const formik = useFormik({
        initialValues: {
            customerName: '',
            phoneNumber: ''
        },
        validationSchema,
        onSubmit: () => {
            if (step === 'otp') {
                verifyOtpHandler(formik.values.phoneNumber);
            }
        }
    });
    const sendOtpHandler = async (phoneNumber) => {
        setOtp((prev) => ({ ...prev, loading: true }));
        const payload = { phoneno: phoneNumber };
        const response = await marketPlaceSendOtpApi(payload);
        if (response.status === 200) {
            globalNotifySuccess(response?.data?.message);
            setStep('otp');
        }
        setOtp((prev) => ({ ...prev, loading: false }));
    };
    const verifyOtpHandler = async (phoneno) => {
        setOtp((prev) => ({ ...prev, loading: true }));
        const payload = { otp: otp.value, phoneno };
        const response = await marketPlaceVerifyOtpApi(payload);
        if (response?.status === 200) {
            globalNotifySuccess(response?.data?.message);
            const checkSumValue = response.data?.checksum;
            setIsSubmitted(true);
            submitDetailsHandler({ checkSumValue });
            setTimeout(() => {
                setIsSubmitted(false);
                setStep('input');
                formik.resetForm();
                setOtp((prev) => ({ ...prev, value: '' }));
            }, 3000);
        }
        setOtp((prev) => ({ ...prev, loading: false }));
    };
    const submitDetailsHandler = async ({ checkSumValue }) => {
        const payload = {
            service_id: displayData?.id,
            phoneCheckSum: checkSumValue,
            customer_name: formik.values.customerName,
            phoneno: formik.values.phoneNumber
        };
        const response = await storeMarketCustomerApi(payload);
        if (response?.status === 200) {
            setSubmitSuccess('completed');
            globalNotifySuccess(response?.data);
            window.open(displayData?.url, '_blank');
        } else {
            setSubmitSuccess('failed');
        }
    };
    return (
        <Box className={styles.container}>
            <Container maxWidth="md" className={styles.contentContainer}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: 'easeOut' }}>
                    <Paper className={styles.mainCard} elevation={3}>
                        {/* Back Button */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4 }}
                            className={styles.backButtonContainer}
                        >
                            <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                <IconButton
                                    onClick={() => {
                                        navigate(-1);
                                    }}
                                    className={styles.backButton}
                                    size="large"
                                >
                                    <ArrowBack />
                                </IconButton>
                            </motion.div>
                        </motion.div>
                        {/* Header */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1, duration: 0.5 }}
                        >
                            <Box className={styles.header}>
                                <Typography variant="h4" className={styles.title}>
                                    {displayData?.name}
                                </Typography>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Avatar className={styles.logo}>
                                        <img src={displayData?.logo} alt="Service Logo" className={styles.logoImage} />
                                    </Avatar>
                                </motion.div>
                            </Box>
                        </motion.div>

                        {/* Commission Display */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2, duration: 0.5 }}
                        >
                            <Card className={styles.commissionCard}>
                                <CardContent className={styles.commissionContent}>
                                    <Typography variant="subtitle1" className={styles.commissionLabel}>
                                        {` Commission ${displayData?.type === 'percentage' ? 'Percentage' : 'Amount'}`}
                                    </Typography>
                                    <Typography variant="h3" className={styles.commissionAmount}>
                                        {displayData?.type === 'percentage'
                                            ? `From ${displayData?.amount?.toLocaleString()} %  to ${displayData?.max_amount?.toLocaleString()} %`
                                            : `Up to ₹ ${displayData?.max_amount?.toLocaleString()}`}
                                    </Typography>
                                    <Chip label="Will be added to wallet" className={styles.statusChip} size="small" />
                                </CardContent>
                            </Card>
                        </motion.div>

                        <AnimatePresence mode="wait">
                            {!isSubmitted ? (
                                <motion.form
                                    key="form"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    onSubmit={formik.handleSubmit}
                                    className={styles.formContainer}
                                >
                                    {/* Customer Name Field */}
                                    <motion.div
                                        initial={{ opacity: 0, x: -15 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.3, duration: 0.4 }}
                                    >
                                        <TextField
                                            fullWidth
                                            name="customerName"
                                            label="Customer Name"
                                            variant="outlined"
                                            value={formik.values.customerName}
                                            onChange={formik.handleChange}
                                            disabled={step === 'otp'}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.customerName && Boolean(formik.errors.customerName)}
                                            helperText={formik.touched.customerName && formik.errors.customerName}
                                            className={styles.formField}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person className={styles.inputIcon} />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </motion.div>

                                    {/* Phone Number Field */}
                                    <motion.div
                                        initial={{ opacity: 0, x: -15 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.4, duration: 0.4 }}
                                    >
                                        <TextField
                                            fullWidth
                                            name="phoneNumber"
                                            label="Phone Number"
                                            variant="outlined"
                                            disabled={step === 'otp'}
                                            value={formik.values.phoneNumber}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                                            helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                                            className={styles.formField}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Phone className={styles.inputIcon} />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </motion.div>
                                    {step === 'otp' && (
                                        <motion.div
                                            initial={{ opacity: 0, x: -15 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.45, duration: 0.4 }}
                                        >
                                            <TextField
                                                fullWidth
                                                label="Enter OTP"
                                                variant="outlined"
                                                value={otp.value}
                                                onChange={(e) => {
                                                    setOtp((prev) => ({ ...prev, value: e.target.value }));
                                                    setOtpError('');
                                                }}
                                                error={!!otpError}
                                                helperText={otpError || 'We’ve sent a 6-digit code to your phone.'}
                                                className={styles.formField}
                                                inputProps={{ maxLength: 6 }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <VpnKey className={styles.inputIcon} />
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                className={styles.resendLink}
                                                onClick={() => sendOtpHandler(formik.values.phoneNumber)}
                                            >
                                                Resend OTP
                                            </Typography>
                                        </motion.div>
                                    )}

                                    {/* Submit Button */}
                                    <motion.div
                                        initial={{ opacity: 0, y: 15 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.5, duration: 0.4 }}
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                    >
                                        <LoadingButton
                                            fullWidth
                                            variant="contained"
                                            loading={otp.loading}
                                            onClick={() => {
                                                if (step === 'input') {
                                                    if (!formik.isValid) return;
                                                    // Simulate OTP send
                                                    sendOtpHandler(formik.values.phoneNumber);
                                                } else {
                                                    formik.handleSubmit();
                                                }
                                            }}
                                            disabled={step === 'input' ? !formik.isValid || !formik.dirty : otp.value.length !== 6}
                                            className={styles.submitButton}
                                            startIcon={step === 'otp' ? <CheckCircle /> : <Send />}
                                            size="large"
                                        >
                                            {step === 'input' ? 'Send OTP' : 'Verify & Submit'}
                                        </LoadingButton>
                                    </motion.div>
                                </motion.form>
                            ) : (
                                <motion.div
                                    key="success"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <Card
                                        className={`${styles.statusCard} ${
                                            submitSuccess === 'failed' ? styles.failedCard : styles.successCard
                                        }`}
                                    >
                                        <CardContent className={styles.successContent}>
                                            {submitSuccess === 'failed' ? (
                                                <ErrorIcon className={styles.failedIcon} />
                                            ) : (
                                                <CheckCircle className={styles.successIcon} />
                                            )}
                                            <Typography
                                                variant="h6"
                                                className={submitSuccess === 'failed' ? styles.failedTitle : styles.successTitle}
                                            >
                                                {submitSuccess === 'failed' ? 'Verification Failed' : 'OTP Verified Successfully'}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                className={submitSuccess === 'failed' ? styles.failedMessage : styles.successMessage}
                                            >
                                                {submitSuccess === 'failed'
                                                    ? 'Submission failed, please try again.'
                                                    : 'You will be redirected to your service shortly.'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Paper>
                </motion.div>
            </Container>
            {/* <Spinner loading={otp.loading} /> */}
        </Box>
    );
};

export default AddNewCustomerEntry;
